import twitterText from 'twitter-text'

import { DraftMethods } from '~publish/legacy/composer/composer/entities/Draft/DraftMethods'
import type {
  Gif,
  Link,
  Image,
} from '~publish/legacy/composer/composer/entities/factories'
import {
  SERVICE_INSTAGRAM,
  SERVICE_LINKEDIN,
  SERVICE_FACEBOOK,
  SERVICE_GOOGLEBUSINESS,
  SERVICE_STARTPAGE,
  SERVICE_SUBSTACK,
  SERVICE_TIKTOK,
  SERVICE_YOUTUBE,
} from '~publish/legacy/constants'
import { getMastodonMentions } from '~publish/legacy/utils/channels/mastodon/helpers'

import type { Content } from '../types'

export const DEFAULT_AVATAR =
  'https://s3.amazonaws.com/buffer-ui/Default+Avatar.png'

export const gifAsImage = (gif: Gif): Image => ({
  url: gif.url,
  width: gif.width,
  height: gif.height,
  mediaType: gif.mediaType,
})

export const hasOnlyUrl = (text: string): boolean => {
  const parsedUrlsWithIndices = twitterText.extractUrlsWithIndices(text)
  const urls = parsedUrlsWithIndices?.map(
    (urlWithIndices) => urlWithIndices.url,
  )

  if (urls.length && urls.length > 0) {
    let newText = text
    urls.forEach((url) => {
      newText = newText.replace(url, '')
    })
    if (newText.replace(/\s/g, '').length === 0) {
      return true
    }
  }

  return false
}

export const extractUrl = (
  text: string,
  returnFirstUrl?: boolean,
): string | null => {
  const parsedUrlsWithIndices = twitterText.extractUrlsWithIndices(text)
  const urls = parsedUrlsWithIndices?.map(
    (urlWithIndices) => urlWithIndices.url,
  )

  if (urls.length && urls.length > 0) {
    return returnFirstUrl ? urls[0] : urls[urls.length - 1]
  }

  return null
}

export const textExceedsCharLimit = (
  text: string,
  charLimit: number,
): boolean => {
  return twitterText.getTweetLength(text) > charLimit
}

export const truncateText = (
  text: string | undefined,
  charLimit: number,
  appendEllipsis = false,
): string | undefined => {
  let textToDisplay = text

  const hasTooLongText =
    textToDisplay && textExceedsCharLimit(textToDisplay, charLimit)

  if (hasTooLongText) {
    textToDisplay = textToDisplay?.substring(0, charLimit)
    textToDisplay = appendEllipsis ? `${textToDisplay}...` : textToDisplay
  }

  return textToDisplay
}

export const truncateBlueskyText = (
  text: string | undefined,
  charLimit: number,
): string | undefined => {
  if (!text) {
    return text
  }

  const characterCount = DraftMethods.getBlueskyTextCharacterCount(text)
  const hasTooLongText = characterCount > charLimit

  if (hasTooLongText) {
    // take into consideration that emojis are counted as one character and links take max 31 characters
    return text.substring(0, charLimit + (text.length - characterCount))
  }

  return text
}

export const truncateMastodonText = (
  text: string | undefined,
  charLimit: number,
): string | undefined => {
  if (!text) return text

  const parsedUrlsWithIndices = twitterText.extractUrlsWithIndices(text)
  const allMentions = getMastodonMentions(text) || []

  // Filter out mentions whose indices overlap with URL entities
  // to prevent @ symbols inside URLs from being counted as mentions
  const mentions = allMentions.filter((mention) => {
    return !parsedUrlsWithIndices.some((urlEntity) => {
      const [mentionStart, mentionEnd] = mention.indices
      const [urlStart, urlEnd] = urlEntity.indices
      return mentionStart < urlEnd && urlStart < mentionEnd
    })
  })

  if (parsedUrlsWithIndices.length === 0 && mentions.length === 0) {
    return truncateText(text, charLimit)
  }

  const linkLength = 23
  let tempText = text
  let characterCount = 0

  parsedUrlsWithIndices.forEach((urlEntity) => {
    characterCount += linkLength
    tempText = tempText.replace(urlEntity.url, '')
  })

  mentions.forEach((mention) => {
    characterCount += mention.displayString.length
    tempText = tempText.replace(mention.rawString, '')
  })

  characterCount += tempText.length
  const exceededCharacterCount = characterCount - charLimit

  if (exceededCharacterCount > 0) {
    return text.slice(0, -exceededCharacterCount)
  }

  return text
}

export const getMediaProperties = (
  content: Content,
  linkPreview: Link | null,
): {
  hasImages: boolean
  hasGif: boolean
  hasVideo: boolean
  hasLink: boolean
} => {
  const { images, gif, video, enabledAttachmentType } = content

  const hasMedia = enabledAttachmentType === 'MEDIA'
  const hasImages = !!(hasMedia && images?.length)
  const hasGif = !!(hasMedia && gif)
  const hasVideo = !!(hasMedia && video)
  const hasLink = !!(linkPreview && !hasImages && !hasVideo)

  return { hasImages, hasGif, hasVideo, hasLink }
}

export enum FormatLayout {
  Column = 'column',
  Row = 'row',
}

export enum ImageOrientation {
  Square = 'square',
  Portrait = 'portrait',
  Landscape = 'landscape',
}

export const getOrientation = ({
  height,
  width,
}: {
  height?: number | null
  width?: number | null
}): ImageOrientation => {
  if (!height || !width || width > height) {
    return ImageOrientation.Landscape
  }

  if (height === width) {
    return ImageOrientation.Square
  }

  return ImageOrientation.Portrait
}

// Instagram feed post aspect ratio limits — images outside this range get
// cropped by Instagram itself, so match that behaviour in the preview.
const INSTAGRAM_MIN_RATIO = 0.8 // 4:5 portrait
const INSTAGRAM_MAX_RATIO = 1.91 // 1.91:1 landscape
const INSTAGRAM_CONTAINER_WIDTH = 316
// Matches the legacy landscape bucket height — used when we don't yet know
// the image's dimensions (some upload flows populate them asynchronously).
const INSTAGRAM_FALLBACK_HEIGHT = 161

/**
 * Calculates the preview container height from the image's actual dimensions,
 * clamped to Instagram's supported feed aspect ratios. Falls back to the
 * legacy landscape bucket when dimensions are missing so existing behaviour
 * is preserved.
 */
export const getInstagramPreviewHeight = ({
  height,
  width,
}: {
  height?: number | null
  width?: number | null
}): number => {
  if (!height || !width) {
    return INSTAGRAM_FALLBACK_HEIGHT
  }
  const ratio = Math.min(
    INSTAGRAM_MAX_RATIO,
    Math.max(INSTAGRAM_MIN_RATIO, width / height),
  )
  return Math.round(INSTAGRAM_CONTAINER_WIDTH / ratio)
}

export const isImagePortrait = (image: Image): boolean =>
  getOrientation(image) === ImageOrientation.Portrait

export const isImageLandscape = (image: Image): boolean =>
  getOrientation(image) === ImageOrientation.Landscape

export const isImageSquare = (image: Image): boolean =>
  getOrientation(image) === ImageOrientation.Square

export const removeLinkFromEndOfText = (
  text: string,
  link?: string | null,
): string => {
  if (link && text.trim().endsWith(link)) {
    text = text.replace(link, '').trim()
  }
  return text
}

export const getPreviewCharLimit = (service: string): number => {
  switch (service) {
    case SERVICE_INSTAGRAM:
    case SERVICE_LINKEDIN:
    case SERVICE_FACEBOOK:
    case SERVICE_STARTPAGE:
      return 100
    case SERVICE_GOOGLEBUSINESS:
      return 220
    case SERVICE_TIKTOK:
      return 84
    case 'twitter':
      return 280
    case SERVICE_SUBSTACK:
      return 500
    default:
      return 125
  }
}

export const getPreviewTitleCharLimit = (service: string): number => {
  switch (service) {
    case SERVICE_YOUTUBE:
      return 100
    default:
      return 58
  }
}
